import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-4064">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4065">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-4066">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-4067">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-4068" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-4069">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4070" />
              <p className="text-sm text-gray-600" data-id="element-4071">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-4072">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4073" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-4074">
          {/* Quality & Metadata */}
          <div data-id="element-4075">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4076">
              <Brain className="w-3.5 h-3.5" data-id="element-4077" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-4078">
              <div className="grid grid-cols-2 gap-4" data-id="element-4079">
                <div data-id="element-4080">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4081">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4082">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-4083">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4084">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-4085">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-4086">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4087">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div className="col-span-2" data-id="element-4088">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4089">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4090">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-4091">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4092">
              <Database className="w-3.5 h-3.5" data-id="element-4093" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-4094">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4095" />
                <span className="text-sm text-gray-600" data-id="element-4096">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-4097">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-4098">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-4099">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4100">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-4101" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-4102">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-4103">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4104" />
                  <span className="text-sm" data-id="element-4105">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-4106">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4107" />
                  <span className="text-sm" data-id="element-4108">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}